import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, keyframes, Tooltip, useMediaQuery } from '@mui/material';

// px per second; keeps short and long overflows at a similar reading speed
const SCROLL_SPEED = 40;

const createScrollAnimation = (distance) => keyframes`
  0%, 15% { transform: translateX(0); }
  85%, 100% { transform: translateX(-${distance}px); }
`;

/**
 * Single-line text that shows an ellipsis when it doesn't fit and scrolls to
 * reveal the rest only on demand: while hovered (mouse) or once after a tap
 * (touch). A permanently running marquee kept the GPU busy for every long
 * title on screen, even in hidden elements.
 */
const MarqueeText = ({ text, style, className }) => {
  const containerRef = useRef(null);
  const [overflow, setOverflow] = useState(0);
  // 'off' | 'hover' (runs while hovered) | 'once' (one pass after a tap)
  const [mode, setMode] = useState('off');
  const prefersReducedMotion = useMediaQuery(
    '(prefers-reduced-motion: reduce)',
    { noSsr: true },
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const measure = () =>
      setOverflow(Math.max(0, container.scrollWidth - container.clientWidth));
    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [text]);

  const animation = useMemo(
    () => (overflow > 0 ? createScrollAnimation(overflow) : null),
    [overflow],
  );

  const canScroll = Boolean(animation) && !prefersReducedMotion;
  const isRunning = canScroll && mode !== 'off';
  const duration = Math.max(1.5, (overflow / SCROLL_SPEED) * 1.4);

  return (
    <Tooltip title={overflow > 0 ? text : ''} placement="top" arrow disableTouchListener>
      <Box
        ref={containerRef}
        className={className}
        // pointer events, not mouse events: iOS emulates mouseenter after a
        // tap, which would start the endless hover mode on touch
        onPointerEnter={(e) =>
          e.pointerType === 'mouse' && canScroll && setMode('hover')
        }
        onPointerLeave={(e) => e.pointerType === 'mouse' && setMode('off')}
        onPointerUp={(e) => {
          if (e.pointerType !== 'mouse' && canScroll && mode === 'off')
            setMode('once');
        }}
        sx={{
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: isRunning ? 'clip' : 'ellipsis',
          width: '100%',
          position: 'relative',
          display: 'block',
        }}
      >
        <Box
          component="span"
          style={style}
          onAnimationEnd={() => setMode('off')}
          sx={{
            display: isRunning ? 'inline-block' : 'inline',
            whiteSpace: 'nowrap',
            ...(isRunning && {
              animation: `${animation} ${duration}s ease-in-out ${
                mode === 'hover' ? 'infinite alternate' : '2 alternate'
              }`,
            }),
          }}
        >
          {text}
        </Box>
      </Box>
    </Tooltip>
  );
};

export default MarqueeText;
